import { CheckCircle2, ExternalLink, Eye, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  managerPodService,
  podBooksService,
  type ManagerPodDashboardResponse,
  type ManagerPodPreviewResponse,
  type ManagerPodRequestsResponse,
  type PodBookListing,
  type PodPaymentStatus,
  type PodPrintStatus,
  type PodProjectStatus,
} from '../../../shared/services';

type FeedbackStatus = 'idle' | 'pending' | 'success' | 'error';

interface FeedbackState {
  status: FeedbackStatus;
  message: string;
}

const initialFeedback: FeedbackState = { status: 'idle', message: '' };

const requestPaymentFilters: Array<'all' | PodPaymentStatus> = ['all', 'pending', 'paid', 'failed'];
const requestPrintFilters: Array<'all' | PodPrintStatus> = ['all', 'not_sent', 'in_production', 'shipped', 'delivered', 'cancelled'];

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) {
    return '-';
  }
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(parsedDate);
};

const formatCurrency = (amount: number, currency?: string | null): string => {
  const resolvedCurrency = (currency || '$').toUpperCase();
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: resolvedCurrency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${resolvedCurrency} ${amount.toFixed(2)}`;
  }
};

const formatStatusLabel = (value: string): string =>
  value
    .split('_')
    .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
    .join(' ');

const getStatusTone = (value: string): string => {
  const normalized = value.trim().toLowerCase();
  if (
    normalized === 'approved' ||
    normalized === 'paid' ||
    normalized === 'passed' ||
    normalized === 'delivered' ||
    normalized === 'shipped'
  ) {
    return 'bg-emerald-100 text-emerald-700';
  }
  if (normalized === 'rejected' || normalized === 'failed' || normalized === 'cancelled' || normalized === 'invalid') {
    return 'bg-red-100 text-red-700';
  }
  if (normalized === 'under_review' || normalized === 'pending' || normalized === 'in_production' || normalized === 'not_sent') {
    return 'bg-amber-100 text-amber-700';
  }
  return 'bg-slate-100 text-slate-700';
};

const toErrorMessage = (error: unknown, fallbackMessage: string): string => {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallbackMessage;
};

const PodManagerDashboardPanel = () => {
  const { t } = useTranslation();

  const [dashboard, setDashboard] = useState<ManagerPodDashboardResponse | null>(null);
  const [preview, setPreview] = useState<ManagerPodPreviewResponse | null>(null);
  const [requests, setRequests] = useState<ManagerPodRequestsResponse | null>(null);
  const [catalogBooks, setCatalogBooks] = useState<PodBookListing[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');

  const [dashboardPage, setDashboardPage] = useState(1);
  const [dashboardLimit, setDashboardLimit] = useState(12);
  const [dashboardStatus, setDashboardStatus] = useState<'all' | PodProjectStatus>('all');
  const [dashboardSearch, setDashboardSearch] = useState('');

  const [requestPage, setRequestPage] = useState(1);
  const [requestLimit, setRequestLimit] = useState(10);
  const [requestPaymentStatus, setRequestPaymentStatus] = useState<'all' | PodPaymentStatus>('all');
  const [requestPrintStatus, setRequestPrintStatus] = useState<'all' | PodPrintStatus>('all');
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogFilter, setCatalogFilter] = useState<'all' | 'pending' | 'approved'>('all');
  const [activeCatalogActionId, setActiveCatalogActionId] = useState<string | null>(null);

  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  const [isCatalogLoading, setIsCatalogLoading] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isRequestsLoading, setIsRequestsLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [feedback, setFeedback] = useState<FeedbackState>(initialFeedback);

  const selectedPod = useMemo(() => {
    if (!dashboard || !selectedProjectId) {
      return null;
    }
    return dashboard.pods.find((pod) => pod.project.id === selectedProjectId) ?? null;
  }, [dashboard, selectedProjectId]);

  const requestSummary = requests?.summary ?? preview?.requestSummary ?? null;
  const publishedCatalogBooks = useMemo(
    () => catalogBooks.filter((book) => book.approvedByAdmin || book.podStatus === 'published'),
    [catalogBooks],
  );
  const pendingCatalogBooks = useMemo(
    () => catalogBooks.filter((book) => !book.approvedByAdmin && book.podStatus !== 'published'),
    [catalogBooks],
  );
  const visibleCatalogBooks = useMemo(() => {
    const normalizedSearch = catalogSearch.trim().toLowerCase();

    return catalogBooks.filter((book) => {
      const matchesFilter =
        catalogFilter === 'all'
          ? true
          : catalogFilter === 'approved'
            ? book.approvedByAdmin || book.podStatus === 'published'
            : !book.approvedByAdmin && book.podStatus !== 'published';

      if (!matchesFilter) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      return [book.title, book.author, book.id, book.size, book.coverType]
        .filter(Boolean)
        .some((value) => value?.toLowerCase().includes(normalizedSearch));
    });
  }, [catalogBooks, catalogFilter, catalogSearch]);

  const resolveContentTitle = (titleKey: string | null | undefined, nameKey: string | null | undefined): string => {
    const key = titleKey || nameKey;
    if (!key) {
      return 'Untitled';
    }
    const translated = t(key);
    if (translated !== key) {
      return translated;
    }
    const fallback = key
      .split('.')
      .at(-1)
      ?.replace(/[-_]+/g, ' ')
      .trim();
    return fallback && fallback.length > 0 ? fallback : key;
  };

  useEffect(() => {
    let isActive = true;

    const loadCatalogBooks = async () => {
      setIsCatalogLoading(true);
      try {
        const data = await podBooksService.getPodBooks();
        if (!isActive) {
          return;
        }
        setCatalogBooks(data);
      } catch (error: unknown) {
        if (!isActive) {
          return;
        }
        setFeedback((previous) =>
          previous.status === 'error'
            ? previous
            : {
                status: 'error',
                message: toErrorMessage(error, 'Unable to load live POD books.'),
              },
        );
      } finally {
        if (isActive) {
          setIsCatalogLoading(false);
        }
      }
    };

    void loadCatalogBooks();

    return () => {
      isActive = false;
    };
  }, [refreshTick]);

  useEffect(() => {
    let isActive = true;

    const loadDashboard = async () => {
      setIsDashboardLoading(true);
      setFeedback({ status: 'pending', message: 'Loading POD dashboard...' });
      try {
        const data = await managerPodService.getDashboard({
          page: dashboardPage,
          limit: dashboardLimit,
          status: dashboardStatus === 'all' ? undefined : dashboardStatus,
          search: dashboardSearch.trim() || undefined,
        });
        if (!isActive) {
          return;
        }

        setDashboard(data);
        setFeedback(initialFeedback);
        setSelectedProjectId((previousProjectId) => {
          if (previousProjectId && data.pods.some((pod) => pod.project.id === previousProjectId)) {
            return previousProjectId;
          }
          return data.pods[0]?.project.id ?? '';
        });
      } catch (error: unknown) {
        if (!isActive) {
          return;
        }
        setFeedback({
          status: 'error',
          message: toErrorMessage(error, 'Unable to load POD dashboard data.'),
        });
      } finally {
        if (isActive) {
          setIsDashboardLoading(false);
        }
      }
    };

    void loadDashboard();

    return () => {
      isActive = false;
    };
  }, [dashboardLimit, dashboardPage, dashboardSearch, dashboardStatus, refreshTick]);

  useEffect(() => {
    setRequestPage(1);
  }, [selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId) {
      setPreview(null);
      return;
    }

    let isActive = true;

    const loadPreview = async () => {
      setIsPreviewLoading(true);
      try {
        const data = await managerPodService.getProjectPreview(selectedProjectId);
        if (!isActive) {
          return;
        }
        setPreview(data);
      } catch (error: unknown) {
        if (!isActive) {
          return;
        }
        setPreview(null);
        setFeedback({
          status: 'error',
          message: toErrorMessage(error, 'Unable to load POD preview data.'),
        });
      } finally {
        if (isActive) {
          setIsPreviewLoading(false);
        }
      }
    };

    void loadPreview();

    return () => {
      isActive = false;
    };
  }, [refreshTick, selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId) {
      setRequests(null);
      return;
    }

    let isActive = true;

    const loadRequests = async () => {
      setIsRequestsLoading(true);
      try {
        const data = await managerPodService.getProjectRequests(selectedProjectId, {
          page: requestPage,
          limit: requestLimit,
          paymentStatus: requestPaymentStatus === 'all' ? undefined : requestPaymentStatus,
          printStatus: requestPrintStatus === 'all' ? undefined : requestPrintStatus,
        });
        if (!isActive) {
          return;
        }

        setRequests(data);
      } catch (error: unknown) {
        if (!isActive) {
          return;
        }
        setRequests(null);
        setFeedback({
          status: 'error',
          message: toErrorMessage(error, 'Unable to load requests for the selected POD project.'),
        });
      } finally {
        if (isActive) {
          setIsRequestsLoading(false);
        }
      }
    };

    void loadRequests();

    return () => {
      isActive = false;
    };
  }, [requestLimit, requestPage, requestPaymentStatus, requestPrintStatus, refreshTick, selectedProjectId]);

  const handlePreviewBook = (book: PodBookListing) => {
    const previewUrl = book.interiorPreviewUrl ?? book.coverPreviewUrl;
    if (!previewUrl) {
      setFeedback({ status: 'error', message: `No preview file is available for "${book.title}".` });
      return;
    }

    window.open(previewUrl, '_blank', 'noopener,noreferrer');
  };

  const handleApproveBook = async (book: PodBookListing) => {
    setActiveCatalogActionId(book.id);
    setFeedback({ status: 'pending', message: `Approving "${book.title}"...` });

    try {
      await podBooksService.approvePodBook(book.id);
      setFeedback({ status: 'success', message: `"${book.title}" approved successfully.` });
      setRefreshTick((previous) => previous + 1);
    } catch (error: unknown) {
      setFeedback({ status: 'error', message: toErrorMessage(error, 'Unable to approve POD book.') });
    } finally {
      setActiveCatalogActionId(null);
    }
  };

  const handleDeleteBook = async (book: PodBookListing) => {
    const confirmed = typeof window === 'undefined' ? true : window.confirm(`Delete "${book.title}" from POD uploads?`);
    if (!confirmed) {
      return;
    }

    setActiveCatalogActionId(book.id);
    setFeedback({ status: 'pending', message: `Deleting "${book.title}"...` });

    try {
      await podBooksService.deletePodBook(book.id);
      setFeedback({ status: 'success', message: `"${book.title}" deleted successfully.` });
      setRefreshTick((previous) => previous + 1);
    } catch (error: unknown) {
      setFeedback({ status: 'error', message: toErrorMessage(error, 'Unable to delete POD book.') });
    } finally {
      setActiveCatalogActionId(null);
    }
  };

  const loadingState = isDashboardLoading && !dashboard;
  const dashboardTotalPages = dashboard?.pagination.totalPages ?? 1;
  const requestsTotalPages = requests?.pagination.totalPages ?? 1;

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
      <div className="min-w-0 space-y-4 pb-2">
        <div className="grid gap-4 md:grid-cols-3">
          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Live POD Books</p>
            <p className="mt-2 text-2xl font-semibold text-slate-900">{catalogBooks.length}</p>
            <p className="mt-1 text-xs text-slate-500">Fetched from `/api/pod/books`</p>
          </article>
          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Published Catalog</p>
            <p className="mt-2 text-2xl font-semibold text-slate-900">{publishedCatalogBooks.length}</p>
            <p className="mt-1 text-xs text-slate-500">Ready for storefront visibility</p>
          </article>
          <article className="rounded-2xl border border-orange-100 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pending Catalog</p>
            <p className="mt-2 text-2xl font-semibold text-slate-900">{pendingCatalogBooks.length}</p>
            <p className="mt-1 text-xs text-slate-500">Needs admin review or publishing</p>
          </article>
        </div>

        <article className="rounded-2xl border border-orange-100 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">All POD Uploads</h3>
              <p className="mt-1 text-xs text-slate-500">Review every uploaded POD title and moderate it with preview, approve, and delete actions.</p>
            </div>
            {isCatalogLoading ? <p className="text-xs font-semibold text-orange-600">Refreshing catalog...</p> : null}
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_180px]">
            <label className="grid gap-1 text-sm">
              <span className="text-slate-600">Search uploads</span>
              <input
                value={catalogSearch}
                onChange={(event) => setCatalogSearch(event.target.value)}
                placeholder="Search by title, author, or ID..."
                className="rounded-xl border border-orange-100 px-3 py-2 focus:border-orange-400 focus:outline-none"
              />
            </label>
            <label className="grid gap-1 text-sm">
              <span className="text-slate-600">Review filter</span>
              <select
                value={catalogFilter}
                onChange={(event) => setCatalogFilter(event.target.value as 'all' | 'pending' | 'approved')}
                className="rounded-xl border border-orange-100 bg-white px-3 py-2 focus:border-orange-400 focus:outline-none"
              >
                <option value="all">All uploads</option>
                <option value="pending">Pending review</option>
                <option value="approved">Approved</option>
              </select>
            </label>
          </div>

          {visibleCatalogBooks.length === 0 ? (
            <div className="mt-4 rounded-2xl border border-dashed border-orange-200 bg-orange-50/50 px-4 py-6 text-sm text-slate-600">
              No POD uploads match the current search or filter.
            </div>
          ) : (
            <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {visibleCatalogBooks.map((book) => {
                const isApproved = book.approvedByAdmin || book.podStatus === 'published';
                const isActionPending = activeCatalogActionId === book.id;

                return (
                <article key={book.id} className="overflow-hidden rounded-2xl border border-orange-100 bg-[#fffaf6]">
                  <div className="relative">
                    <img src={book.imageUrl} alt={book.title} className="h-44 w-full object-cover" />
                    <div className="absolute left-3 top-3">
                      <span className={`inline-flex rounded-full px-2 py-1 text-[11px] font-semibold ${getStatusTone(book.podStatus)}`}>
                        {formatStatusLabel(book.podStatus)}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-3 p-4">
                    <div>
                      <p className="line-clamp-1 text-sm font-semibold text-slate-900">{book.title}</p>
                      <p className="mt-1 line-clamp-1 text-xs text-slate-500">{book.author}</p>
                    </div>
                    <div className="flex flex-wrap gap-2 text-[11px] font-semibold text-slate-500">
                      {book.size ? <span className="rounded-full bg-orange-50 px-2 py-1 text-orange-700">{book.size}</span> : null}
                      {book.pageCount ? <span>{book.pageCount} pages</span> : null}
                      {book.coverType ? <span>{formatStatusLabel(book.coverType)}</span> : null}
                    </div>
                    <div className="flex items-end justify-between gap-3">
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Print price</p>
                        <p className="mt-1 text-lg font-bold text-slate-900">{formatCurrency(book.printPrice, 'RWF')}</p>
                      </div>
                    </div>
                    <div className="grid gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => handlePreviewBook(book)}
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-orange-200 px-3 py-2 text-xs font-semibold text-orange-700 transition hover:bg-orange-50"
                      >
                        <Eye className="h-4 w-4" />
                        Preview
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleApproveBook(book)}
                        disabled={isApproved || isActionPending}
                        className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold transition ${
                          isApproved
                            ? 'cursor-not-allowed bg-emerald-100 text-emerald-700'
                            : 'bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-70'
                        }`}
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        {isApproved ? 'Approved' : isActionPending ? 'Approving...' : 'Approve'}
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDeleteBook(book)}
                        disabled={isActionPending}
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70"
                      >
                        <Trash2 className="h-4 w-4" />
                        {isActionPending ? 'Working...' : 'Delete'}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2 pt-2">
                      {book.coverPreviewUrl ? (
                        <a
                          href={book.coverPreviewUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 rounded-lg border border-orange-200 px-2 py-1 text-[11px] font-semibold text-orange-700 transition hover:bg-orange-50"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          Cover File
                        </a>
                      ) : null}
                      {book.interiorPreviewUrl ? (
                        <a
                          href={book.interiorPreviewUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 rounded-lg border border-orange-200 px-2 py-1 text-[11px] font-semibold text-orange-700 transition hover:bg-orange-50"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          Interior File
                        </a>
                      ) : null}
                    </div>
                  </div>
                </article>
                );
              })}
            </div>
          )}
        </article>

        {loadingState ? (
          <article className="rounded-2xl border border-orange-100 bg-white p-6 text-center text-sm text-slate-600">
            Loading POD dashboard...
          </article>
        ) : !dashboard ? (
          <article className="rounded-2xl border border-orange-100 bg-white p-6 text-center text-sm text-slate-600">
            No POD dashboard data available.
          </article>
        ) : (
          <>
            <div className="grid gap-4 md:grid-cols-4">
              <article className="rounded-2xl border border-orange-100 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total Projects</p>
                <p className="mt-2 text-2xl font-semibold text-slate-900">{dashboard?.overview.totalProjects ?? 0}</p>
              </article>
              <article className="rounded-2xl border border-orange-100 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total Requests</p>
                <p className="mt-2 text-2xl font-semibold text-slate-900">{dashboard?.overview.totalRequests ?? 0}</p>
              </article>
              <article className="rounded-2xl border border-orange-100 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Under Review</p>
                <p className="mt-2 text-2xl font-semibold text-slate-900">{dashboard?.overview.projectsByStatus.under_review ?? 0}</p>
              </article>
              <article className="rounded-2xl border border-orange-100 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Print Queue</p>
                <p className="mt-2 text-2xl font-semibold text-slate-900">
                  {(dashboard?.overview.requestsByPrintStatus.not_sent ?? 0) +
                    (dashboard?.overview.requestsByPrintStatus.in_production ?? 0)}
                </p>
              </article>
            </div>

            <article className="overflow-x-auto rounded-2xl border border-orange-100 bg-white">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-orange-100 px-4 py-3">
                <h3 className="text-sm font-semibold text-slate-900">All POD Projects</h3>
                <p className="text-xs text-slate-500">
                  Page {dashboardPage} of {dashboardTotalPages}
                </p>
              </div>

              <table className="min-w-full text-left text-sm">
                <thead className="bg-orange-50 text-xs uppercase tracking-wide text-slate-600">
                  <tr>
                    <th className="px-4 py-3">Project</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Creator</th>
                    <th className="px-4 py-3">Requests</th>
                    <th className="px-4 py-3">Preflight</th>
                    <th className="px-4 py-3">Listing</th>
                    <th className="px-4 py-3">Preview</th>
                  </tr>
                </thead>
                <tbody>
                  {isDashboardLoading ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                        Loading POD projects...
                      </td>
                    </tr>
                  ) : dashboard.pods.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                        No POD projects found for this filter.
                      </td>
                    </tr>
                  ) : (
                    dashboard.pods.map((pod) => {
                      const creatorName =
                        pod.project.creator?.fullname ||
                        pod.project.creator?.username ||
                        pod.project.creator?.email ||
                        'Unknown';
                      const isSelected = selectedProjectId === pod.project.id;
                      return (
                        <tr key={pod.project.id} className="border-t border-orange-100 align-top">
                          <td className="px-4 py-3">
                            <p className="font-semibold text-slate-900">{pod.project.title || 'Untitled project'}</p>
                            <p className="text-xs text-slate-500">{pod.project.id}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusTone(pod.project.status)}`}>
                              {formatStatusLabel(pod.project.status)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-600">{creatorName}</td>
                          <td className="px-4 py-3 text-xs text-slate-600">{pod.requests.totalRequests}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusTone(pod.preflightStatus)}`}>
                              {formatStatusLabel(pod.preflightStatus)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-600">
                            {pod.listing ? formatCurrency(pod.listing.price, pod.listing.currency) : 'No listing'}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => setSelectedProjectId(pod.project.id)}
                              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                                isSelected ? 'bg-orange-500 text-white' : 'border border-orange-200 text-orange-700 hover:bg-orange-50'
                              }`}
                            >
                              {isSelected ? 'Selected' : 'Open Preview'}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
              <div className="flex items-center justify-end gap-2 border-t border-orange-100 px-4 py-3">
                <button
                  type="button"
                  onClick={() => setDashboardPage((value) => Math.max(1, value - 1))}
                  disabled={dashboardPage <= 1}
                  className="rounded-lg border border-orange-200 px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setDashboardPage((value) => Math.min(dashboardTotalPages, value + 1))}
                  disabled={dashboardPage >= dashboardTotalPages}
                  className="rounded-lg border border-orange-200 px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Next
                </button>
              </div>
            </article>

            <article className="overflow-x-auto rounded-2xl border border-orange-100 bg-white">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-orange-100 px-4 py-3">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">Requests for Selected POD</h3>
                  <p className="mt-1 text-xs text-slate-500">{preview?.project.title || selectedPod?.project.title || 'No project selected'}</p>
                </div>
                <div className="grid gap-2 sm:grid-cols-3">
                  <select
                    value={requestPaymentStatus}
                    onChange={(event) => {
                      setRequestPaymentStatus(event.target.value as 'all' | PodPaymentStatus);
                      setRequestPage(1);
                    }}
                    className="rounded-lg border border-orange-200 bg-white px-2 py-1.5 text-xs text-slate-700 focus:border-orange-400 focus:outline-none"
                  >
                    {requestPaymentFilters.map((status) => (
                      <option key={status} value={status}>
                        Payment: {status === 'all' ? 'All' : formatStatusLabel(status)}
                      </option>
                    ))}
                  </select>
                  <select
                    value={requestPrintStatus}
                    onChange={(event) => {
                      setRequestPrintStatus(event.target.value as 'all' | PodPrintStatus);
                      setRequestPage(1);
                    }}
                    className="rounded-lg border border-orange-200 bg-white px-2 py-1.5 text-xs text-slate-700 focus:border-orange-400 focus:outline-none"
                  >
                    {requestPrintFilters.map((status) => (
                      <option key={status} value={status}>
                        Print: {status === 'all' ? 'All' : formatStatusLabel(status)}
                      </option>
                    ))}
                  </select>
                  <select
                    value={requestLimit}
                    onChange={(event) => {
                      setRequestLimit(Number(event.target.value));
                      setRequestPage(1);
                    }}
                    className="rounded-lg border border-orange-200 bg-white px-2 py-1.5 text-xs text-slate-700 focus:border-orange-400 focus:outline-none"
                  >
                    {[10, 20, 30].map((size) => (
                      <option key={size} value={size}>
                        Rows: {size}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <table className="min-w-full text-left text-sm">
                <thead className="bg-orange-50 text-xs uppercase tracking-wide text-slate-600">
                  <tr>
                    <th className="px-4 py-3">Request</th>
                    <th className="px-4 py-3">User</th>
                    <th className="px-4 py-3">Item</th>
                    <th className="px-4 py-3">Quantity</th>
                    <th className="px-4 py-3">Total</th>
                    <th className="px-4 py-3">Payment</th>
                    <th className="px-4 py-3">Print</th>
                    <th className="px-4 py-3">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {isRequestsLoading ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                        Loading project requests...
                      </td>
                    </tr>
                  ) : !requests || requests.requests.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                        No requests found for selected filters.
                      </td>
                    </tr>
                  ) : (
                    requests.requests.map((request) => (
                      <tr key={request.id} className="border-t border-orange-100 align-top">
                        <td className="px-4 py-3">
                          <p className="font-semibold text-slate-900">{request.id}</p>
                          <p className="text-xs text-slate-500">{request.provider || 'Provider not set'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-800">{request.user?.fullname || request.user?.username || 'Unknown'}</p>
                          <p className="text-xs text-slate-500">{request.user?.email || '-'}</p>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600">
                          {resolveContentTitle(request.product?.titleKey, request.product?.nameKey)}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600">{request.quantity}</td>
                        <td className="px-4 py-3 text-xs font-semibold text-slate-800">
                          {formatCurrency(request.totalPrice, request.listing?.currency)}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusTone(request.paymentStatus)}`}>
                            {formatStatusLabel(request.paymentStatus)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusTone(request.printStatus)}`}>
                            {formatStatusLabel(request.printStatus)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600">{formatDateTime(request.createdAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              <div className="flex items-center justify-end gap-2 border-t border-orange-100 px-4 py-3">
                <button
                  type="button"
                  onClick={() => setRequestPage((value) => Math.max(1, value - 1))}
                  disabled={requestPage <= 1}
                  className="rounded-lg border border-orange-200 px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setRequestPage((value) => Math.min(requestsTotalPages, value + 1))}
                  disabled={requestPage >= requestsTotalPages}
                  className="rounded-lg border border-orange-200 px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Next
                </button>
              </div>
            </article>
          </>
        )}
      </div>

      <aside className="min-w-0 space-y-4">
        <article className="rounded-2xl border border-orange-100 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-900">Selected POD Preview</h3>
          {isPreviewLoading ? (
            <p className="mt-3 text-xs text-slate-500">Loading preview...</p>
          ) : !preview ? (
            <p className="mt-3 text-xs text-slate-500">Select a POD project to preview details.</p>
          ) : (
            <div className="mt-3 space-y-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">{preview.project.title || 'Untitled project'}</p>
                <p className="text-xs text-slate-500">{preview.project.id}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className={`rounded-full px-2 py-1 text-[11px] font-semibold ${getStatusTone(preview.project.status)}`}>
                  {formatStatusLabel(preview.project.status)}
                </span>
                <span className={`rounded-full px-2 py-1 text-[11px] font-semibold ${getStatusTone(preview.preflightStatus)}`}>
                  Preflight: {formatStatusLabel(preview.preflightStatus)}
                </span>
              </div>
              <div className="space-y-1 text-xs text-slate-600">
                <p>
                  Listing:{' '}
                  {preview.listing ? `${formatCurrency(preview.listing.price, preview.listing.currency)}` : 'No listing yet'}
                </p>
                <p>Latest request: {formatDateTime(preview.requestSummary.latestRequestAt)}</p>
              </div>
              <div className="space-y-2">
                {preview.preview.cover?.fileUrl ? (
                  <a
                    href={preview.preview.cover.fileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded-lg border border-orange-200 px-2 py-1 text-xs font-semibold text-orange-700 transition hover:bg-orange-50"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Open Cover
                  </a>
                ) : (
                  <p className="text-xs text-slate-500">Cover file not available.</p>
                )}
                {preview.preview.interior?.fileUrl ? (
                  <a
                    href={preview.preview.interior.fileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-2 inline-flex items-center gap-1 rounded-lg border border-orange-200 px-2 py-1 text-xs font-semibold text-orange-700 transition hover:bg-orange-50"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Open Interior
                  </a>
                ) : (
                  <p className="text-xs text-slate-500">Interior file not available.</p>
                )}
              </div>
            </div>
          )}
        </article>

        <article className="rounded-2xl border border-orange-100 bg-white p-4">
          <h3 className="text-sm font-semibold text-slate-900">Requests Summary</h3>
          {!requestSummary ? (
            <p className="mt-3 text-xs text-slate-500">No request summary available yet.</p>
          ) : (
            <ul className="mt-3 space-y-2 text-xs text-slate-600">
              <li className="rounded-lg bg-orange-50 p-2">Total requests: {requestSummary.totalRequests}</li>
              <li className="rounded-lg bg-orange-50 p-2">Total quantity: {requestSummary.totalQuantity}</li>
              <li className="rounded-lg bg-orange-50 p-2">Total revenue: {formatCurrency(requestSummary.totalRevenue, preview?.listing?.currency)}</li>
              <li className="rounded-lg bg-orange-50 p-2">Latest: {formatDateTime(requestSummary.latestRequestAt)}</li>
            </ul>
          )}
        </article>
      </aside>
    </div>
  );
};

export default PodManagerDashboardPanel;
